import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { CALENDLY_LINK } from '../../../utils/constants';

const CaseStudyModal = ({ caseStudy, isOpen, onClose }) => {
  if (!isOpen || !caseStudy) return null;

  const timelineSteps = [
    { phase: 'Initial Assessment', duration: '1-2 weeks', status: 'completed' },
    { phase: 'Data Collection', duration: '2-3 weeks', status: 'completed' },
    { phase: 'Valuation Analysis', duration: '3-4 weeks', status: 'completed' },
    { phase: 'Report Preparation', duration: '1-2 weeks', status: 'completed' },
    { phase: 'Final Review', duration: '1 week', status: 'completed' }
  ];

  const methodologyPoints = [
    'Applied IVS 2022 standards throughout the engagement',
    'Utilized multiple valuation approaches for cross-validation',
    'Incorporated GCC market-specific risk factors',
    'Conducted comprehensive peer analysis',
    'Applied appropriate discount rates for regional markets'
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-background/80 backdrop-blur-sm" onClick={onClose}></div>

        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-card shadow-brand-lg rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-12 h-12 bg-accent/10 rounded-lg">
                <Icon name="FileText" size={24} className="text-accent" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-text-primary">
                  {caseStudy?.title}
                </h2>
                <p className="text-sm text-text-secondary">
                  {caseStudy?.industry} • {caseStudy?.serviceType}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              iconName="X"
              iconSize={20}
            />
          </div>

          {/* Content */}
          <div className="max-h-[70vh] overflow-y-auto">
            <div className="p-6 space-y-8">
              {/* Challenge & Context */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                  <Icon name="AlertCircle" size={20} className="mr-2 text-warning" />
                  Challenge & Context
                </h3>
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-text-secondary leading-relaxed">
                    {caseStudy?.detailedChallenge}
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <div className="text-center p-3 bg-surface rounded-lg">
                    <div className="text-sm font-medium text-text-primary">Company Size</div>
                    <div className="text-xs text-text-secondary mt-1">{caseStudy?.companySize}</div>
                  </div>
                  <div className="text-center p-3 bg-surface rounded-lg">
                    <div className="text-sm font-medium text-text-primary">Location</div>
                    <div className="text-xs text-text-secondary mt-1">{caseStudy?.location}</div>
                  </div>
                  <div className="text-center p-3 bg-surface rounded-lg">
                    <div className="text-sm font-medium text-text-primary">Duration</div>
                    <div className="text-xs text-text-secondary mt-1">{caseStudy?.duration}</div>
                  </div>
                  <div className="text-center p-3 bg-surface rounded-lg">
                    <div className="text-sm font-medium text-text-primary">Year</div>
                    <div className="text-xs text-text-secondary mt-1">{caseStudy?.year}</div>
                  </div>
                </div>
              </div>

              {/* Methodology Applied */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                  <Icon name="Settings" size={20} className="mr-2 text-accent" />
                  Methodology Applied
                </h3>
                <div className="space-y-3">
                  {methodologyPoints?.map((point, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="flex items-center justify-center w-6 h-6 bg-success/10 rounded-full mt-0.5">
                        <Icon name="Check" size={14} className="text-success" />
                      </div>
                      <p className="text-text-secondary text-sm">{point}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Project Timeline */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                  <Icon name="Clock" size={20} className="mr-2 text-primary" />
                  Project Timeline
                </h3>
                <div className="space-y-4">
                  {timelineSteps?.map((step, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <div className="flex items-center justify-center w-8 h-8 bg-success/10 rounded-full">
                        <Icon name="CheckCircle" size={16} className="text-success" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-text-primary">{step?.phase}</h4>
                          <span className="text-xs text-text-secondary">{step?.duration}</span>
                        </div>
                        {index < timelineSteps?.length - 1 && (
                          <div className="w-px h-6 bg-border ml-4 mt-2"></div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Unique Considerations */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                  <Icon name="Lightbulb" size={20} className="mr-2 text-warning" />
                  Unique Considerations
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {caseStudy?.uniqueConsiderations?.map((consideration, index) => (
                    <div key={index} className="bg-surface rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <Icon name="Star" size={16} className="text-warning mt-1" />
                        <p className="text-text-secondary text-sm">{consideration}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Outcomes & Results */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                  <Icon name="Target" size={20} className="mr-2 text-success" />
                  Outcomes & Results
                </h3>
                <div className="bg-success/5 border border-success/20 rounded-lg p-6">
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-success mb-2">
                        {caseStudy?.outcome?.valueRange}
                      </div>
                      <div className="text-sm text-text-secondary">Final Valuation</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-success mb-2">
                        {caseStudy?.outcome?.timeline}
                      </div>
                      <div className="text-sm text-text-secondary">Ahead of Schedule</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-success mb-2">
                        {caseStudy?.outcome?.satisfaction}
                      </div>
                      <div className="text-sm text-text-secondary">Client Satisfaction</div>
                    </div>
                  </div>
                  <div className="mt-6 pt-6 border-t border-success/20">
                    <blockquote className="text-text-secondary italic text-center">
                      "{caseStudy?.clientTestimonial}"
                    </blockquote>
                    <div className="text-center mt-3">
                      <div className="text-sm font-medium text-text-primary">
                        {caseStudy?.clientRole}
                      </div>
                      <div className="text-xs text-text-secondary">
                        {caseStudy?.clientCompany}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-border bg-muted">
            <div className="text-sm text-text-secondary">
              Need similar expertise for your project?
            </div>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
                iconSize={16}
              >
                Download Summary
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Calendar"
                iconPosition="left"
                iconSize={16}
                asChild
              >
                <Link to="/book-consultation">
                  Free Consultation
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseStudyModal;